// src/components/social/SocialFeed.jsx
import React, { useEffect, useState } from "react";
import apiClient from "../../Api/apiConfig";
import { useAuth } from "../../lib/auth";
import PostComposer from "./PostComposer";
import "../../styles/social.css";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:5000";

export default function SocialFeed() {
  const { user } = useAuth();

  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // commentaires
  const [commentsByPost, setCommentsByPost] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [openComments, setOpenComments] = useState({});

  // édition
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingContent, setEditingContent] = useState("");

  const loadFeed = async () => {
    try {
      setLoading(true);

      const response = await apiClient.get("/social/feed");
      console.log("SOCIAL FEED RAW =", response.data);

      const posts = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.posts)
        ? response.data.posts
        : [];

      setFeed(posts);
      setError(null);
    } catch (err) {
      console.error("Erreur chargement feed :", err);
      setError("Impossible de charger le fil pour le moment.");
      setFeed([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeed();
  }, []);

  const handlePostCreated = (newPost) => {
    setFeed((prev) => {
      const filtered = prev.filter((p) => p.id !== newPost.id);
      return [newPost, ...filtered];
    });
  };

  // ---- LIKE ----
  const handleToggleLike = async (postId) => {
    try {
      const res = await apiClient.post(`/social/posts/${postId}/like`);
      const liked = !!res.data?.liked;

      setFeed((prev) =>
        prev.map((post) => {
          if (post.id !== postId) return post;

          const currentCount = Number(post.like_count || 0);
          let newCount = liked ? currentCount + 1 : currentCount - 1;
          if (newCount < 0) newCount = 0;

          return {
            ...post,
            has_liked: liked ? 1 : 0,
            like_count: newCount,
          };
        })
      );
    } catch (err) {
      console.error("Erreur toggle like :", err);
      alert("Impossible de liker ce post pour le moment.");
    }
  };

  // ---- COMMENTS ----
  const loadComments = async (postId) => {
    try {
      const res = await apiClient.get(`/social/posts/${postId}/comments`);
      const comments = res.data?.comments || [];
      setCommentsByPost((prev) => ({ ...prev, [postId]: comments }));
    } catch (err) {
      console.error("Erreur chargement commentaires :", err);
    }
  };

  const handleToggleComments = async (postId) => {
    setOpenComments((prev) => {
      const isOpen = !prev[postId];
      return { ...prev, [postId]: isOpen };
    });

    if (!openComments[postId]) {
      await loadComments(postId);
    }
  };

  const handleCommentChange = (postId, value) => {
    setCommentInputs((prev) => ({ ...prev, [postId]: value }));
  };

  const handleSubmitComment = async (postId) => {
    const content = (commentInputs[postId] || "").trim();
    if (!content) return;

    try {
      const res = await apiClient.post(`/social/posts/${postId}/comments`, {
        content,
      });

      const newComment = res.data;

      setCommentsByPost((prev) => ({
        ...prev,
        [postId]: [...(prev[postId] || []), newComment],
      }));

      setFeed((prev) =>
        prev.map((post) =>
          post.id === postId
            ? {
                ...post,
                comment_count: Number(post.comment_count || 0) + 1,
              }
            : post
        )
      );

      setCommentInputs((prev) => ({ ...prev, [postId]: "" }));
    } catch (err) {
      console.error("Erreur création commentaire :", err);
      alert("Impossible de commenter pour le moment.");
    }
  };

  // ---- EDIT POST ----
  const startEditPost = (post) => {
    setEditingPostId(post.id);
    setEditingContent(post.content || "");
  };

  const cancelEditPost = () => {
    setEditingPostId(null);
    setEditingContent("");
  };

  const handleSavePost = async (postId) => {
    const newContent = editingContent.trim();
    if (!newContent) {
      alert("Le contenu ne peut pas être vide.");
      return;
    }

    try {
      await apiClient.patch(`/social/posts/${postId}`, {
        content: newContent,
      });

      setFeed((prev) =>
        prev.map((post) =>
          post.id === postId ? { ...post, content: newContent } : post
        )
      );

      cancelEditPost();
    } catch (err) {
      console.error("Erreur modification post :", err);
      alert("Impossible de modifier ce post pour le moment.");
    }
  };

  // ---- DELETE POST ----
  const handleDeletePost = async (postId) => {
    if (!window.confirm("Supprimer ce post ?")) return;

    try {
      await apiClient.delete(`/social/posts/${postId}`);

      setFeed((prev) => prev.filter((p) => p.id !== postId));

      setCommentsByPost((prev) => {
        const copy = { ...prev };
        delete copy[postId];
        return copy;
      });
    } catch (err) {
      console.error("Erreur suppression post :", err);
      alert("Impossible de supprimer ce post pour le moment.");
    }
  };

  if (loading) {
    return <div className="social-feed-container">Chargement du fil…</div>;
  }

  return (
    <div className="social-feed-container">
      <PostComposer onPostCreated={handlePostCreated} />

      {error && <div className="social-error">{error}</div>}

      {feed.length === 0 ? (
        <div className="no-posts">Aucun post pour l’instant.</div>
      ) : (
        feed.map((post) => {
          const rawMedia = post.media_url || post.mediaUrl || null;
          const mediaUrl = rawMedia
            ? rawMedia.startsWith("http")
              ? rawMedia
              : `${API_BASE_URL}${rawMedia}`
            : null;

          const authorName =
            post.author_email ||
            post.author_name ||
            user?.email ||
            "Membre NMS06";

          const comments = commentsByPost[post.id] || [];
          const commentValue = commentInputs[post.id] || "";
          const isCommentsOpen = !!openComments[post.id];

          const likeCount = Number(post.like_count || 0);
          const commentCount = Number(post.comment_count || 0);
          const hasLiked = !!post.has_liked;

          const isAuthor =
            user && String(post.author_id) === String(user.id);

          return (
            <div className="post-card" key={post.id}>
              {/* Header */}
              <div className="post-header">
                <div className="avatar">
                  {authorName?.[0]?.toUpperCase() || "N"}
                </div>
                <div className="post-header-text">
                  <div className="post-author">{authorName}</div>
                  <div className="post-meta">
                    {post.created_at
                      ? new Date(post.created_at).toLocaleString("fr-FR")
                      : ""}
                  </div>
                </div>
              </div>

              {/* Contenu */}
              <div className="post-content">
                {editingPostId === post.id ? (
                  <div className="post-edit-block">
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                    />
                    <div className="post-edit-actions">
                      <button
                        type="button"
                        onClick={() => handleSavePost(post.id)}
                      >
                        Enregistrer
                      </button>
                      <button type="button" onClick={cancelEditPost}>
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  post.content && <p>{post.content}</p>
                )}

                {mediaUrl && (
                  <div className="post-image-wrapper">
                    <img src={mediaUrl} alt="Illustration du post" />
                  </div>
                )}
              </div>

              {/* Actions principales */}
              <div className="post-actions">
                <button
                  type="button"
                  className={hasLiked ? "liked" : ""}
                  onClick={() => handleToggleLike(post.id)}
                >
                  👍 J’aime {likeCount > 0 && <span>• {likeCount}</span>}
                </button>

                <button
                  type="button"
                  onClick={() => handleToggleComments(post.id)}
                >
                  💬 Commenter{" "}
                  {commentCount > 0 && <span>• {commentCount}</span>}
                </button>
              </div>

              {/* Actions secondaires (auteur + signaler) */}
              <div className="post-owner-actions">
                {isAuthor && (
                  <>
                    <button
                      type="button"
                      className="post-owner-btn"
                      onClick={() => startEditPost(post)}
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      type="button"
                      className="post-owner-btn delete"
                      onClick={() => handleDeletePost(post.id)}
                    >
                      🗑️ Supprimer
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="post-owner-btn disabled"
                  title="Bientôt disponible"
                >
                  🚩 Signaler
                </button>
              </div>

              {/* Commentaires */}
              {isCommentsOpen && (
                <div className="comment-section">
                  {comments.length > 0 ? (
                    comments.map((c) => (
                      <div className="comment-item" key={c.id}>
                        <div className="avatar-small">
                          {(c.author_name || "M")[0].toUpperCase()}
                        </div>
                        <div className="comment-body">
                          <div className="comment-author">
                            {c.author_name || "Membre"}
                          </div>
                          <div className="comment-text">{c.content}</div>
                          <div className="comment-meta">
                            {c.created_at
                              ? new Date(c.created_at).toLocaleString("fr-FR")
                              : ""}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-comments">
                      Aucun commentaire pour l’instant.
                    </div>
                  )}

                  <div className="comment-form">
                    <input
                      type="text"
                      placeholder="Écrire un commentaire..."
                      value={commentValue}
                      onChange={(e) =>
                        handleCommentChange(post.id, e.target.value)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => handleSubmitComment(post.id)}
                    >
                      Publier
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
